import React, { useState, useEffect, useMemo } from 'react';
import { ShieldCheck, Wrench, AlertOctagon, Mail, ClipboardCheck, Plus, Trash2, Calendar, Gauge, X, Printer, Search, BellRing, Send, RefreshCw, Loader2, Archive } from 'lucide-react';
import ArchiveerAutoDialog from '../components/ArchiveerAutoDialog';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirm';

const daysUntil = (d) => {
  if (!d) return null;
  const dt = new Date(d); if (isNaN(dt)) return null;
  return Math.floor((dt - new Date().setHours(0, 0, 0, 0)) / 86400000);
};
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const fmt = (d) => d ? new Date(d).toLocaleDateString('nl-NL') : '—';

const apkStatus = (a) => {
  const dd = daysUntil(a.apkVervalt);
  if (dd === null) return { key: 'onbekend', dd, label: 'Onbekend', c: '#64748b', bg: '#f1f5f9' };
  if (dd < 0) return { key: 'verlopen', dd, label: 'Verlopen', c: '#b91c1c', bg: '#fef2f2' };
  if (dd <= 60) return { key: 'binnenkort', dd, label: `Over ${dd} dagen`, c: '#b45309', bg: '#fffbeb' };
  return { key: 'ok', dd, label: `Over ${dd} dagen`, c: '#15803d', bg: '#f0fdf4' };
};

const onderhoudStatus = (a) => {
  const im = parseInt(a.onderhoudIntervalMaanden) || 0;
  const ik = parseInt(a.onderhoudIntervalKm) || 0;
  if (!a.laatsteOnderhoud && !a.laatsteOnderhoudKm) return { key: 'onbekend', label: 'Niet ingesteld', c: '#64748b', bg: '#f1f5f9', detail: '' };
  let dueDate = null, kmLeft = null;
  if (a.laatsteOnderhoud && im) dueDate = addMonths(a.laatsteOnderhoud, im);
  if (a.laatsteOnderhoudKm && ik) kmLeft = (parseInt(a.laatsteOnderhoudKm) + ik) - (parseInt(a.kmStand) || 0);
  const dd = dueDate ? daysUntil(dueDate) : null;
  const overdueDate = dd !== null && dd < 0;
  const overdueKm = kmLeft !== null && kmLeft < 0;
  const soonDate = dd !== null && dd <= 30 && dd >= 0;
  const soonKm = kmLeft !== null && kmLeft <= 1000 && kmLeft >= 0;
  const detail = [dueDate ? `${fmt(dueDate)}` : null, kmLeft !== null ? `${kmLeft >= 0 ? 'nog ' : ''}${kmLeft.toLocaleString('nl-NL')} km` : null].filter(Boolean).join(' · ');
  if (overdueDate || overdueKm) return { key: 'verlopen', label: 'Onderhoud verlopen', c: '#b91c1c', bg: '#fef2f2', detail, dueDate };
  if (soonDate || soonKm) return { key: 'binnenkort', label: 'Binnenkort', c: '#b45309', bg: '#fffbeb', detail, dueDate };
  return { key: 'ok', label: 'Op schema', c: '#15803d', bg: '#f0fdf4', detail, dueDate };
};

const CHECK_ITEMS = ['Verlichting (koplampen, achter, richting)', 'Banden (profiel ≥1,6mm + spanning)', 'Remwerking & remvloeistof', 'Ruitenwissers & sproeiers', 'Voorruit (zichtveld)', 'Veiligheidsgordels', 'Claxon', 'Stuurinrichting (speling)', 'Ophanging / schokdempers', 'Uitlaat / emissie', 'Carrosserie / roest', 'Kentekenplaten & verlichting', 'Vloeistofniveaus (olie, koelvloeistof)', 'Accu & laadspanning'];
const CHECK_STATUS = ['Goed', 'Aandacht', 'Afgekeurd', 'N.v.t.'];

export default function ApkOnderhoud({ onNavigate }) {
  const { t } = useT();
  const { klantAutos: alleAutos, klanten, gegevens, sendEmail, refreshAll } = useData();
  const klantAutos = useMemo(() => (alleAutos || []).filter(a => !a.gearchiveerd), [alleAutos]);
  const [archiveerAuto, setArchiveerAuto] = useState(null);
  const [tab, setTab] = useState('apk');
  const [q, setQ] = useState('');
  const [recalls, setRecalls] = useState([]);
  const [recallForm, setRecallForm] = useState(null);
  const [checklistAuto, setChecklistAuto] = useState(null);
  const [apkRefreshing, setApkRefreshing] = useState(false);

  const refreshApkFromRdw = async () => {
    if (apkRefreshing) return;
    setApkRefreshing(true);
    try {
      const res = await carsysApi.refreshApkRdw();
      await refreshAll(true);
      if (res.updated > 0) toast.success(t(`${res.updated} APK-datum(s) bijgewerkt via RDW`));
      else toast.success(t(`Alle ${res.checked} kentekens gecontroleerd — alles up-to-date`));
    } catch (e) {
      toast.error(t('APK-check mislukt. Probeer later opnieuw.'));
    } finally {
      setApkRefreshing(false);
    }
  };

  useEffect(() => { carsysApi.getRecalls().then(setRecalls).catch(() => setRecalls([])); }, []);

  const klantById = useMemo(() => Object.fromEntries((klanten || []).map(k => [k.id, k])), [klanten]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (klantAutos || []).filter(a => !s || `${a.kenteken} ${a.merk} ${a.model} ${klantById[a.klantId]?.naam || ''}`.toLowerCase().includes(s));
  }, [klantAutos, q, klantById]);

  const apkList = useMemo(() => [...filtered].sort((a, b) => (a.apkVervalt || '9999').localeCompare(b.apkVervalt || '9999')), [filtered]);
  const ohList = useMemo(() => filtered.map(a => ({ a, st: onderhoudStatus(a) }))
    .sort((x, y) => (x.st.dueDate || new Date('2999')) - (y.st.dueDate || new Date('2999'))), [filtered]);

  const apkCounts = useMemo(() => {
    const c = { verlopen: 0, binnenkort: 0 };
    (klantAutos || []).forEach(a => { const s = apkStatus(a).key; if (s === 'verlopen') c.verlopen++; else if (s === 'binnenkort') c.binnenkort++; });
    return c;
  }, [klantAutos]);

  const matchRecall = (r) => (klantAutos || []).filter(a => {
    if (!a.merk || !r.merk) return false;
    if (!a.merk.toLowerCase().includes(r.merk.toLowerCase())) return false;
    if (r.model && !(a.model || '').toLowerCase().includes(r.model.toLowerCase())) return false;
    const bj = parseInt(a.bouwjaar) || 0;
    if (r.bouwjaarVan && bj && bj < parseInt(r.bouwjaarVan)) return false;
    if (r.bouwjaarTot && bj && bj > parseInt(r.bouwjaarTot)) return false;
    return true;
  });

  const sendApkHerinnering = async (a) => {
    const klant = klantById[a.klantId];
    if (!klant?.email) { toast.error(t('Klant heeft geen e-mailadres', 'Klant heeft geen e-mailadres')); return; }
    const garage = gegevens?.bedrijfsnaam || gegevens?.naam || 'uw garage';
    try {
      const res = await sendEmail({
        aan: klant.email, type: 'apk',
        onderwerp: `APK-herinnering ${a.kenteken} — ${garage}`,
        body: `Beste ${klant.naam},\n\nDe APK van uw ${a.merk || ''} ${a.model || ''} (${a.kenteken}) verloopt op ${fmt(a.apkVervalt)}.\nMaak tijdig een afspraak zodat uw auto APK-gekeurd de weg op kan.\n\nMet vriendelijke groet,\n${garage}`,
      });
      const ok = !String(res.status || '').toUpperCase().includes('MOCK');
      ok ? toast.success(t('APK-herinnering verstuurd', 'APK-herinnering verstuurd'), { description: klant.email })
        : toast.warning(t('E-mail niet geconfigureerd', 'E-mail niet geconfigureerd'), { description: res.status });
    } catch (e) { toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const saveRecall = async () => {
    if (!recallForm.titel?.trim() || !recallForm.merk?.trim()) { toast.error(t('Titel en merk zijn verplicht', 'Titel en merk zijn verplicht')); return; }
    try {
      const r = await carsysApi.createRecall(recallForm);
      setRecalls(prev => [...prev, r]); setRecallForm(null);
      toast.success(t('Terugroepactie toegevoegd', 'Terugroepactie toegevoegd'));
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
  };
  const delRecall = async (id) => {
    if (!(await confirmDialog(t('Terugroepactie verwijderen?', 'Terugroepactie verwijderen?')))) return;
    await carsysApi.deleteRecall(id); setRecalls(prev => prev.filter(r => r.id !== id));
    toast.success(t('Verwijderd', 'Verwijderd'));
  };

  return (
    <div className="space-y-4" data-testid="apk-onderhoud-page">
      <div className="cs-page-header">
        <div><h2><ShieldCheck size={20} style={{ verticalAlign: -3, marginRight: 6 }} />{t('APK & Onderhoud', 'APK & Onderhoud')}</h2>
          <p>{apkCounts.verlopen} {t('APK verlopen', 'APK verlopen')} · {apkCounts.binnenkort} {t('binnenkort', 'binnenkort')}</p></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="cs-btn ghost" onClick={refreshApkFromRdw} disabled={apkRefreshing} data-testid="apk-refresh-rdw-btn"
            title={t('Controleer alle kentekens bij de RDW en werk de APK-datums bij')}>
            <RefreshCw size={15} className={apkRefreshing ? 'spin' : ''} /> {apkRefreshing ? t('Bezig…') : t('APK-datums verversen')}
          </button>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
            <input className="cs-input" style={{ paddingLeft: 28, width: 220 }} placeholder={t('Zoek kenteken/merk/klant', 'Zoek kenteken/merk/klant')} value={q} onChange={e => setQ(e.target.value)} data-testid="apk-search" />
          </div>
        </div>
      </div>

      <div className="cs-tabs">
        <button className={`cs-tab ${tab === 'apk' ? 'active' : ''}`} onClick={() => setTab('apk')} data-testid="tab-apk"><ShieldCheck size={14} /> {t('APK-vervaldatums', 'APK-vervaldatums')}</button>
        <button className={`cs-tab ${tab === 'onderhoud' ? 'active' : ''}`} onClick={() => setTab('onderhoud')} data-testid="tab-onderhoud"><Wrench size={14} /> {t('Onderhoud', 'Onderhoud')}</button>
        <button className={`cs-tab ${tab === 'recalls' ? 'active' : ''}`} onClick={() => setTab('recalls')} data-testid="tab-recalls"><AlertOctagon size={14} /> {t('Terugroepacties', 'Terugroepacties')} {recalls.length > 0 && `(${recalls.length})`}</button>
        <button className={`cs-tab ${tab === 'herinneringen' ? 'active' : ''}`} onClick={() => setTab('herinneringen')} data-testid="tab-herinneringen"><BellRing size={14} /> {t('Automatische herinneringen', 'Automatische herinneringen')}</button>
      </div>

      {tab === 'apk' && (
        <div className="cs-card" data-testid="apk-list">
          <table className="cs-table">
            <thead><tr><th>{t('Kenteken')}</th><th>{t('Voertuig', 'Voertuig')}</th><th>{t('Klant')}</th><th>{t('APK vervalt', 'APK vervalt')}</th><th>{t('Status')}</th><th></th></tr></thead>
            <tbody>
              {apkList.map(a => {
                const st = apkStatus(a); const klant = klantById[a.klantId];
                return (
                  <tr key={a.kenteken} data-testid={`apk-row-${a.kenteken}`}>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{a.kenteken}</td>
                    <td>{[a.merk, a.model].filter(Boolean).join(' ') || '—'}</td>
                    <td>{klant?.naam || '—'}</td>
                    <td>{fmt(a.apkVervalt)}</td>
                    <td><span className="cs-badge" style={{ background: st.bg, color: st.c }} data-testid={`apk-status-${a.kenteken}`}>{t(st.label, st.label)}</span></td>
                    <td><div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button className="cs-btn ghost" title={t('APK-voorkeuring checklist', 'APK-voorkeuring checklist')} onClick={() => setChecklistAuto(a)} data-testid={`apk-checklist-${a.kenteken}`}><ClipboardCheck size={15} /></button>
                      <button className="cs-btn ghost" title={t('Stuur herinnering', 'Stuur herinnering')} onClick={() => sendApkHerinnering(a)} disabled={st.key === 'onbekend'} data-testid={`apk-herinner-${a.kenteken}`}><Mail size={15} /></button>
                      <button className="cs-btn ghost" title={t('Archiveren (klant heeft auto niet meer)')} onClick={() => setArchiveerAuto(a)} data-testid={`apk-archiveer-${a.kenteken}`}><Archive size={15} /></button>
                    </div></td>
                  </tr>
                );
              })}
              {apkList.length === 0 && <tr><td colSpan={6} className="cs-empty">{t('Geen voertuigen', 'Geen voertuigen')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'onderhoud' && (
        <div className="cs-card" data-testid="onderhoud-list">
          <table className="cs-table">
            <thead><tr><th>{t('Kenteken')}</th><th>{t('Voertuig', 'Voertuig')}</th><th>{t('Klant')}</th><th>{t('Volgende beurt', 'Volgende beurt')}</th><th>{t('Status')}</th><th></th></tr></thead>
            <tbody>
              {ohList.map(({ a, st }) => {
                const klant = klantById[a.klantId];
                return (
                  <tr key={a.kenteken} data-testid={`onderhoud-row-${a.kenteken}`}>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{a.kenteken}</td>
                    <td>{[a.merk, a.model].filter(Boolean).join(' ') || '—'}</td>
                    <td>{klant?.naam || '—'}</td>
                    <td style={{ fontSize: 12.5, color: '#475569' }}>{st.detail || '—'}</td>
                    <td><span className="cs-badge" style={{ background: st.bg, color: st.c }} data-testid={`onderhoud-status-${a.kenteken}`}>{t(st.label, st.label)}</span></td>
                    <td><div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button className="cs-btn ghost" title={t('Plan afspraak', 'Plan afspraak')} onClick={() => onNavigate && onNavigate('planning')} data-testid={`onderhoud-plan-${a.kenteken}`}><Calendar size={15} /></button>
                      <button className="cs-btn ghost" title={t('Stuur herinnering', 'Stuur herinnering')} onClick={() => sendApkHerinnering(a)} data-testid={`onderhoud-herinner-${a.kenteken}`}><Mail size={15} /></button>
                    </div></td>
                  </tr>
                );
              })}
              {ohList.length === 0 && <tr><td colSpan={6} className="cs-empty">{t('Geen voertuigen', 'Geen voertuigen')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'recalls' && (
        <div className="space-y-3" data-testid="recalls-list">
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="cs-btn primary" onClick={() => setRecallForm({ titel: '', merk: '', model: '', bouwjaarVan: '', bouwjaarTot: '', referentie: '', omschrijving: '' })} data-testid="recall-add"><Plus size={14} /> {t('Terugroepactie toevoegen', 'Terugroepactie toevoegen')}</button>
          </div>
          {recalls.length === 0 && <div className="cs-card cs-empty" style={{ padding: 28 }}>{t('Nog geen terugroepacties. Voeg een recall toe; het systeem markeert automatisch de getroffen voertuigen in je klantenbestand.', 'Nog geen terugroepacties. Voeg een recall toe; het systeem markeert automatisch de getroffen voertuigen in je klantenbestand.')}</div>}
          {recalls.map(r => {
            const m = matchRecall(r);
            return (
              <div key={r.id} className="cs-card padded" data-testid={`recall-${r.id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}><AlertOctagon size={15} color="#dc2626" style={{ verticalAlign: -2, marginRight: 5 }} />{r.titel}</div>
                    <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>{[r.merk, r.model, (r.bouwjaarVan || r.bouwjaarTot) ? `${r.bouwjaarVan || '…'}–${r.bouwjaarTot || '…'}` : ''].filter(Boolean).join(' · ')}{r.referentie ? ` · ref ${r.referentie}` : ''}</div>
                    {r.omschrijving && <div style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>{r.omschrijving}</div>}
                  </div>
                  <button className="cs-btn ghost" onClick={() => delRecall(r.id)} data-testid={`recall-del-${r.id}`}><Trash2 size={15} color="#dc2626" /></button>
                </div>
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--cs-border)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: m.length ? '#b91c1c' : '#15803d', marginBottom: 6 }} data-testid={`recall-match-${r.id}`}>
                    {m.length} {t('getroffen voertuig(en) in je bestand', 'getroffen voertuig(en) in je bestand')}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {m.map(a => <span key={a.kenteken} className="cs-badge gray" style={{ fontFamily: 'ui-monospace, monospace' }}>{a.kenteken} · {klantById[a.klantId]?.naam || ''}</span>)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'herinneringen' && <ApkHerinneringen t={t} />}

      {recallForm && <RecallForm form={recallForm} setForm={setRecallForm} onSave={saveRecall} onClose={() => setRecallForm(null)} t={t} />}
      {archiveerAuto && <ArchiveerAutoDialog auto={archiveerAuto} onClose={() => setArchiveerAuto(null)} />}
      {checklistAuto && <VoorkeuringChecklist auto={checklistAuto} klant={klantById[checklistAuto.klantId]} gegevens={gegevens} onClose={() => setChecklistAuto(null)} t={t} />}
    </div>
  );
}

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusBadge = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.startsWith('verzonden')) return { c: '#15803d', bg: '#f0fdf4' };
  if (s.startsWith('mislukt')) return { c: '#b91c1c', bg: '#fef2f2' };
  return { c: '#b45309', bg: '#fffbeb' };
};

function ApkHerinneringen({ t }) {
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [dagenInput, setDagenInput] = useState('30, 7');

  const loadLog = async () => {
    try { setLog(await carsysApi.getApkReminderLog()); } catch { setLog([]); }
  };

  useEffect(() => {
    (async () => {
      try {
        const s = await carsysApi.getApkReminderSettings();
        setSettings(s);
        setDagenInput((s.apkHerinneringDagen || [30, 7]).join(', '));
      } catch (e) {
        toast.error(t('Instellingen laden mislukt', 'Instellingen laden mislukt'));
      }
      await loadLog();
      setLoading(false);
    })();
  }, []);

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      const dagen = dagenInput.split(',').map(x => parseInt(x.trim())).filter(n => n > 0);
      const payload = { autoApkHerinneringen: settings.autoApkHerinneringen, apkHerinneringDagen: dagen, ...patch };
      const s = await carsysApi.setApkReminderSettings(payload);
      setSettings(s);
      setDagenInput((s.apkHerinneringDagen || [30, 7]).join(', '));
      toast.success(t('Instellingen opgeslagen', 'Instellingen opgeslagen'));
    } catch (e) {
      toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const toggleAuto = async () => {
    const next = !settings.autoApkHerinneringen;
    setSettings(s => ({ ...s, autoApkHerinneringen: next }));
    await saveSettings({ autoApkHerinneringen: next });
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await carsysApi.runApkReminders();
      const sent = res.sent || 0, vehicles = res.vehicles || 0, geen = res.geenEmail || 0;
      if (sent > 0) toast.success(t('APK-herinneringen verstuurd', 'APK-herinneringen verstuurd'), { description: `${sent} verzonden · ${vehicles} voertuig(en)${geen ? ` · ${geen} zonder e-mail` : ''}` });
      else toast.info(t('Geen herinneringen verstuurd', 'Geen herinneringen verstuurd'), { description: `${vehicles} voertuig(en) in venster${geen ? ` · ${geen} zonder e-mail` : ''}` });
      await loadLog();
    } catch (e) {
      toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message });
    } finally { setRunning(false); }
  };

  if (loading || !settings) return <div className="cs-card cs-empty" style={{ padding: 28 }} data-testid="apk-herinneringen-loading"><Loader2 size={16} className="animate-spin" style={{ verticalAlign: -3, marginRight: 6 }} />{t('Laden…', 'Laden…')}</div>;

  return (
    <div className="space-y-3" data-testid="apk-herinneringen">
      <div className="cs-card padded">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}><BellRing size={16} style={{ verticalAlign: -3, marginRight: 6 }} />{t('Automatische APK-herinneringen', 'Automatische APK-herinneringen')}</div>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>{t('Stuurt klanten automatisch een e-mail wanneer de APK van hun voertuig bijna verloopt. Voertuigen zonder APK-plicht worden overgeslagen.', 'Stuurt klanten automatisch een e-mail wanneer de APK van hun voertuig bijna verloopt. Voertuigen zonder APK-plicht worden overgeslagen.')}</p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }} data-testid="apk-auto-toggle-label">
            <input type="checkbox" checked={!!settings.autoApkHerinneringen} onChange={toggleAuto} disabled={saving} data-testid="apk-auto-toggle" style={{ width: 18, height: 18 }} />
            {settings.autoApkHerinneringen ? t('Dagelijks aan', 'Dagelijks aan') : t('Uitgeschakeld', 'Uitgeschakeld')}
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <div>
            <label className="cs-section-title">{t('Dagen vóór APK-vervaldatum', 'Dagen vóór APK-vervaldatum')}</label>
            <input className="cs-input" style={{ width: 160 }} value={dagenInput} onChange={e => setDagenInput(e.target.value)} placeholder="30, 7" data-testid="apk-dagen-input" />
          </div>
          <button className="cs-btn" onClick={() => saveSettings({})} disabled={saving} data-testid="apk-save-settings">{saving ? <Loader2 size={14} className="animate-spin" /> : null} {t('Instellingen opslaan', 'Instellingen opslaan')}</button>
          <button className="cs-btn primary" onClick={runNow} disabled={running} data-testid="apk-run-now">{running ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t('Stuur APK-herinneringen nu', 'Stuur APK-herinneringen nu')}</button>
        </div>
        {settings.lastApkReminderRun && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>{t('Laatste verzending', 'Laatste verzending')}: {fmtDateTime(settings.lastApkReminderRun)}</div>}
      </div>

      <div className="cs-card" data-testid="apk-log">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--cs-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{t('Laatste verzendingen', 'Laatste verzendingen')}</div>
          <button className="cs-btn ghost" onClick={loadLog} title={t('Vernieuwen', 'Vernieuwen')} data-testid="apk-log-refresh"><RefreshCw size={15} /></button>
        </div>
        <table className="cs-table">
          <thead><tr><th>{t('Datum', 'Datum')}</th><th>{t('Kenteken')}</th><th>{t('Klant')}</th><th>{t('Type', 'Type')}</th><th>{t('Status')}</th></tr></thead>
          <tbody>
            {log.map(l => {
              const b = statusBadge(l.status);
              return (
                <tr key={l.id} data-testid={`apk-log-row-${l.id}`}>
                  <td style={{ fontSize: 12.5, color: '#475569' }}>{fmtDateTime(l.sentAt)}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>{l.kenteken}</td>
                  <td>{l.klantNaam || '—'}</td>
                  <td><span className="cs-badge gray">{l.type === 'handmatig' ? t('Handmatig', 'Handmatig') : t('Automatisch', 'Automatisch')}</span></td>
                  <td><span className="cs-badge" style={{ background: b.bg, color: b.c }} data-testid={`apk-log-status-${l.id}`}>{l.status}</span></td>
                </tr>
              );
            })}
            {log.length === 0 && <tr><td colSpan={5} className="cs-empty">{t('Nog geen herinneringen verstuurd', 'Nog geen herinneringen verstuurd')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecallForm({ form, setForm, onSave, onClose, t }) {
  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="recall-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{t('Terugroepactie', 'Terugroepactie')}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Titel', 'Titel')} *</label><input className="cs-input" value={form.titel} onChange={e => u('titel', e.target.value)} data-testid="recall-titel" /></div>
          <div><label className="cs-section-title">{t('Merk')} *</label><input className="cs-input" value={form.merk} onChange={e => u('merk', e.target.value)} data-testid="recall-merk" /></div>
          <div><label className="cs-section-title">{t('Model')}</label><input className="cs-input" value={form.model} onChange={e => u('model', e.target.value)} data-testid="recall-model" /></div>
          <div><label className="cs-section-title">{t('Bouwjaar van', 'Bouwjaar van')}</label><input type="number" className="cs-input" value={form.bouwjaarVan} onChange={e => u('bouwjaarVan', e.target.value)} /></div>
          <div><label className="cs-section-title">{t('Bouwjaar tot', 'Bouwjaar tot')}</label><input type="number" className="cs-input" value={form.bouwjaarTot} onChange={e => u('bouwjaarTot', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Referentie', 'Referentie')}</label><input className="cs-input" value={form.referentie} onChange={e => u('referentie', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / 3' }}><label className="cs-section-title">{t('Omschrijving')}</label><textarea className="cs-input" rows={3} value={form.omschrijving} onChange={e => u('omschrijving', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button className="cs-btn primary" onClick={onSave} data-testid="recall-save">{t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}

function VoorkeuringChecklist({ auto, klant, gegevens, onClose, t }) {
  const [items, setItems] = useState(CHECK_ITEMS.map(naam => ({ naam, status: 'Goed', notitie: '' })));
  const set = (i, k, v) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const afgekeurd = items.filter(i => i.status === 'Afgekeurd').length;
  const aandacht = items.filter(i => i.status === 'Aandacht').length;

  const print = () => {
    const esc = (s) => String(s || '').replace(/</g, '&lt;');
    const rows = items.map(i => `<tr><td>${esc(i.naam)}</td><td class="st st-${i.status}">${i.status}</td><td>${esc(i.notitie)}</td></tr>`).join('');
    const w = window.open('', '_blank', 'width=820,height=1000'); if (!w) return;
    w.document.write(`<!doctype html><html><head><title>APK-voorkeuring ${esc(auto.kenteken)}</title><style>
      @page{size:A4;margin:14mm}*{box-sizing:border-box;font-family:-apple-system,Segoe UI,Roboto,sans-serif}body{margin:0;color:#0f172a}
      h1{font-size:20px;margin:0 0 2px}.sub{color:#64748b;font-size:13px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;font-size:13px}th{text-align:left;background:#f1f5f9;padding:7px 9px}td{padding:7px 9px;border-bottom:1px solid #e2e8f0}
      .st{font-weight:700}.st-Afgekeurd{color:#b91c1c}.st-Aandacht{color:#b45309}.st-Goed{color:#15803d}.st-N\\.v\\.t\\.{color:#64748b}
      .sig{margin-top:40px;display:flex;justify-content:space-between}.sig div{border-top:1px solid #0f172a;padding-top:4px;width:45%;font-size:12px;color:#64748b}
    </style></head><body>
      <h1>${t('APK-voorkeuring checklist', 'APK-voorkeuring checklist')}</h1>
      <div class="sub">${esc(auto.kenteken)} · ${esc([auto.merk, auto.model].filter(Boolean).join(' '))} · ${esc(klant?.naam || '')} · ${new Date().toLocaleDateString('nl-NL')} · ${esc(gegevens?.bedrijfsnaam || gegevens?.naam || 'MonteurCentraal')}</div>
      <table><thead><tr><th>${t('Controlepunt', 'Controlepunt')}</th><th>${t('Status')}</th><th>${t('Notitie', 'Notitie')}</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="sig"><div>${t('Monteur', 'Monteur')}</div><div>${t('Datum', 'Datum')}</div></div>
      <script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>`);
    w.document.close();
  };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }} data-testid="voorkeuring-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}><ClipboardCheck size={17} style={{ verticalAlign: -3, marginRight: 5 }} />{t('APK-voorkeuring', 'APK-voorkeuring')} · {auto.kenteken}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>{aandacht} {t('aandacht', 'aandacht')} · {afgekeurd} {t('afgekeurd', 'afgekeurd')}</div>
        <div className="space-y-2" style={{ maxHeight: '52vh', overflowY: 'auto' }}>
          {items.map((it, i) => (
            <div key={i} data-testid={`check-row-${i}`} style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--cs-border)', paddingBottom: 8 }}>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{it.naam}</div>
                <input className="cs-input" style={{ marginTop: 4, fontSize: 12.5 }} placeholder={t('Notitie', 'Notitie')} value={it.notitie} onChange={e => set(i, 'notitie', e.target.value)} />
              </div>
              <select className="cs-input" value={it.status} onChange={e => set(i, 'status', e.target.value)} data-testid={`check-status-${i}`}
                style={{ fontWeight: 700, color: it.status === 'Afgekeurd' ? '#b91c1c' : it.status === 'Aandacht' ? '#b45309' : it.status === 'Goed' ? '#15803d' : '#64748b' }}>
                {CHECK_STATUS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="cs-btn" onClick={onClose}>{t('Sluiten', 'Sluiten')}</button>
          <button className="cs-btn primary" onClick={print} data-testid="check-print"><Printer size={14} /> {t('Afdrukken', 'Afdrukken')}</button>
        </div>
      </div>
    </div>
  );
}
